import React, { useCallback, useEffect, useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import { billing } from '../../services/superuserApi';

// Alvos de toque ≥ 44 px e texto ≥ 12 px: este painel é lido no telemóvel, a 390 px.
const TAP = 44;

const ICON = {
  plan_change: '📋',
  cycle_change: '🔁',
  renewal: '💰',
  validity_correction: '📅',
  suspension: '⏸',
  reactivation: '▶',
  instance_blocked: '🚫',
  instance_unblocked: '✅',
};

const fmtDateTime = (value) => (value
  ? new Date(value).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

const apiMessage = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

/**
 * Linha do tempo da subscrição de UMA instância.
 *
 * Lê o histórico auditável (`/tenantbilling/instances/{id}/subscription-history`): cada
 * alteração com o ANTES, o DEPOIS, quem a fez, de onde veio (à mão, sistema ou pagamento) e o
 * motivo. É a resposta a «eu renovei em Março» e a «porque é que isto esteve em baixo?».
 *
 * Duas coisas que este painel NÃO faz, e diz:
 *  - sem histórico não inventa linha nenhuma: mostra o estado vazio;
 *  - uma falha de leitura aparece como falha, com repetição — não como «sem alterações».
 *
 * Com `subscriptionId`, mostra também o formulário de correção de validade: sem o motivo escrito
 * não deixa gravar, porque uma correção sem motivo não serve para nada seis meses depois.
 */
const SubscriptionTimeline = ({ instanceId, subscriptionId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [showFix, setShowFix] = useState(false);
  const [fixDate, setFixDate] = useState('');
  const [fixReason, setFixReason] = useState('');
  const [fixProblem, setFixProblem] = useState(null);
  const [fixBusy, setFixBusy] = useState(false);
  const [fixDone, setFixDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await billing.getSubscriptionHistory(instanceId);
      setData({
        events: Array.isArray(res.data?.events) ? res.data.events : [],
        count: res.data?.count || 0,
      });
    } catch (e) {
      setLoadError(apiMessage(e, 'Não foi possível ler o histórico desta instância.'));
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { load(); }, [load]);

  const submitFix = async (event) => {
    event.preventDefault();
    setFixProblem(null);

    if (!fixDate) {
      setFixProblem('Indique a data de validade correta.');
      return;
    }
    if (fixReason.trim().length < 3) {
      setFixProblem('Escreva o motivo da correção (mínimo 3 caracteres): é o que fica no histórico.');
      return;
    }

    setFixBusy(true);
    try {
      const res = await billing.adjustSubscription(subscriptionId, {
        nextBillingDate: fixDate,
        reason: fixReason.trim(),
      });
      setFixDone(res.data?.message || 'Validade corrigida.');
      setFixReason('');
      setShowFix(false);
      await load();
    } catch (e) {
      setFixProblem(apiMessage(e, 'Não foi possível corrigir a validade.'));
    } finally {
      setFixBusy(false);
    }
  };

  const events = data?.events || [];

  return (
    <div data-testid="subscription-timeline" style={styles.card}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <History size={18} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
          <h3 style={styles.title}>Histórico de subscrição</h3>
        </div>
        {subscriptionId ? (
          <button
            type="button"
            data-testid="timeline-fix-toggle"
            onClick={() => { setShowFix((v) => !v); setFixProblem(null); setFixDone(null); }}
            style={styles.secondary}
          >
            {showFix ? 'Fechar' : 'Corrigir validade'}
          </button>
        ) : null}
      </div>

      {loading && (
        <div style={styles.muted}>
          <Loader2 size={16} style={{ animation: 'su-spin 1s linear infinite' }} /> A ler o histórico…
        </div>
      )}

      {!loading && loadError && (
        <div data-testid="timeline-error" style={styles.errorBox}>
          <div>{loadError}</div>
          <button type="button" onClick={load} style={{ ...styles.secondary, marginTop: '0.5rem' }}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !loadError && showFix && (
        <form data-testid="timeline-fix-form" onSubmit={submitFix} style={styles.form}>
          <label htmlFor={`fix-date-${instanceId}`} style={styles.label}>Nova validade (próxima faturação)</label>
          <input
            id={`fix-date-${instanceId}`}
            data-testid="timeline-fix-date"
            type="date"
            value={fixDate}
            onChange={(e) => { setFixDate(e.target.value); setFixProblem(null); }}
            style={styles.input}
            disabled={fixBusy}
          />
          <label htmlFor={`fix-reason-${instanceId}`} style={styles.label}>Motivo</label>
          <input
            id={`fix-reason-${instanceId}`}
            data-testid="timeline-fix-reason"
            type="text"
            placeholder="ex.: acerto do período — acabava a 15, não a 30"
            value={fixReason}
            onChange={(e) => { setFixReason(e.target.value); setFixProblem(null); }}
            style={styles.input}
            disabled={fixBusy}
          />
          {fixProblem && <div data-testid="timeline-fix-problem" style={styles.errorBox}>{fixProblem}</div>}
          <button type="submit" data-testid="timeline-fix-submit" style={styles.primary} disabled={fixBusy}>
            {fixBusy ? 'A corrigir…' : 'Corrigir validade'}
          </button>
        </form>
      )}

      {fixDone && <div data-testid="timeline-fix-done" style={styles.okBox}>{fixDone}</div>}

      {!loading && !loadError && events.length === 0 && (
        <div data-testid="timeline-empty" style={styles.empty}>
          Sem alterações registadas nesta instância. A partir de agora, cada alteração de plano,
          ciclo, validade, suspensão ou bloqueio fica aqui com o antes e o depois.
        </div>
      )}

      {!loading && !loadError && events.length > 0 && (
        <ol data-testid="timeline-events" style={styles.list}>
          {events.map((e) => (
            <li key={e.id} data-testid="timeline-event" style={styles.item}>
              <div style={styles.itemHead}>
                <span style={styles.kind}>
                  <span aria-hidden="true">{ICON[e.kind] || '•'}</span> {e.kindLabel || e.kind}
                </span>
                <span style={styles.when}>{fmtDateTime(e.createdAt)}</span>
              </div>

              <div style={styles.transition}>
                <span style={styles.before}>{e.beforeValue || '—'}</span>
                <span aria-hidden="true" style={styles.arrow}>→</span>
                <span style={styles.after}>{e.afterValue || '—'}</span>
              </div>

              <div style={styles.meta}>
                {e.actor ? <span>{e.actor}</span> : null}
                {e.sourceLabel ? <span style={styles.dot}>·</span> : null}
                {e.sourceLabel ? <span>{e.sourceLabel}</span> : null}
              </div>

              {e.reason ? <div style={styles.reason}>{e.reason}</div> : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

const styles = {
  card: {
    background: 'var(--bg-secondary, #fff)',
    border: '1px solid var(--border, #e3e3e3)',
    borderRadius: '16px',
    padding: '1rem',
    marginTop: '1rem',
    color: 'var(--text-primary, #1c1c1c)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  title: { margin: 0, fontSize: '1rem', fontWeight: 700 },
  muted: {
    marginTop: '0.75rem', fontSize: '0.8125rem', color: 'var(--text-muted, #6b6b6b)',
    display: 'flex', alignItems: 'center', gap: '0.4rem',
  },
  errorBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)', lineHeight: 1.5,
  },
  okBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--success-bg, #e7f6ec)', color: 'var(--success-text, #146c2e)',
  },
  empty: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.8125rem',
    background: 'var(--bg-tertiary, #f7f7f7)', color: 'var(--text-muted, #6b6b6b)', lineHeight: 1.6,
  },
  form: { marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.8125rem', fontWeight: 600, marginTop: '0.35rem' },
  input: {
    minHeight: TAP, padding: '0 0.75rem', borderRadius: '10px', width: '100%', boxSizing: 'border-box',
    border: '1px solid var(--border, #e3e3e3)', background: 'var(--bg-tertiary, #f7f7f7)',
    color: 'var(--text-primary, #1c1c1c)', fontSize: '1rem',
  },
  primary: {
    minHeight: TAP, marginTop: '0.5rem', padding: '0 1rem', borderRadius: '10px', border: 'none',
    background: 'var(--accent-primary, #d4a017)', color: '#fff', fontWeight: 700, fontSize: '0.875rem',
    cursor: 'pointer',
  },
  secondary: {
    minHeight: TAP, minWidth: TAP, padding: '0 1rem', borderRadius: '10px',
    border: '1px solid var(--border, #e3e3e3)', background: 'transparent',
    color: 'var(--text-primary, #1c1c1c)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
  },
  list: { listStyle: 'none', margin: '0.75rem 0 0', padding: 0 },
  item: { padding: '0.75rem 0', borderTop: '1px solid var(--bg-tertiary, #f1f1f1)' },
  itemHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  kind: { fontSize: '0.8125rem', fontWeight: 700 },
  when: { fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)' },
  transition: {
    marginTop: '0.35rem', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem',
    fontSize: '0.8125rem', lineHeight: 1.5,
  },
  before: { color: 'var(--text-muted, #6b6b6b)', textDecoration: 'line-through' },
  arrow: { color: 'var(--accent-primary, #d4a017)', fontWeight: 700 },
  after: { fontWeight: 600 },
  meta: { marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-muted, #6b6b6b)', display: 'flex', gap: '0.35rem', flexWrap: 'wrap' },
  dot: { opacity: 0.6 },
  reason: { marginTop: '0.25rem', fontSize: '0.75rem', color: 'var(--text-primary, #1c1c1c)', fontStyle: 'italic', lineHeight: 1.5 },
};

export default SubscriptionTimeline;
